import { SearchFieldType } from "@/shared/components/filters";
import { TriggerForEachPrimitive } from "@/shared/domain/actions/TriggerRecord";

import { searchFields } from "../types";

/**
 * Resource prefix types for targeting specific resources in conditions.
 *
 * - "file": Targets file properties (requires for_each="dataset_file")
 * - "dataset": Explicitly targets dataset properties
 *
 * Note: "topic" and "message_path" are supported in backend but not yet in UI
 */
export type ResourcePrefix = "file" | "dataset";

/**
 * Returns the default resource prefix based on the trigger's for_each setting.
 *
 * @param forEachPrimitive - The trigger's for_each setting
 * @returns "file" for per-file triggers, "dataset" for per-dataset triggers
 *
 * @example
 * ```typescript
 * getDefaultPrefix(TriggerForEachPrimitive.DatasetFile) // "file"
 * getDefaultPrefix(TriggerForEachPrimitive.Dataset) // "dataset"
 * ```
 */
export function getDefaultPrefix(
  forEachPrimitive: TriggerForEachPrimitive,
): ResourcePrefix {
  return forEachPrimitive === TriggerForEachPrimitive.DatasetFile
    ? "file"
    : "dataset";
}

/**
 * Parses a field name to extract the resource prefix and base field name.
 *
 * Handles the following prefixes:
 * - dataset.* → { prefix: "dataset", baseName: "*" }
 * - file.* → { prefix: "file", baseName: "*" }
 * - * (no prefix) → { prefix: "dataset", baseName: "*" } (defaults to dataset)
 *
 * **Backwards Compatibility Note:**
 * For unprefixed fields created before the resource prefix requirement was introduced,
 * this function now defaults to "dataset" scope instead of returning null. Callers no
 * longer need to special-case null prefixes for backwards compatibility.
 *
 * @param fieldName - The field name to parse (e.g., "file.tags", "tags")
 * @returns Object with prefix and baseName
 *
 * @example
 * ```typescript
 * parseFieldName("file.tags") // { prefix: "file", baseName: "tags" }
 * parseFieldName("tags") // { prefix: "dataset", baseName: "tags" }
 * ```
 */
export function parseFieldName(fieldName: string): {
  prefix: ResourcePrefix;
  baseName: string;
} {
  const match = fieldName.match(/^(dataset|file)\.(.+)$/);
  if (match) {
    return {
      prefix: match[1] as ResourcePrefix,
      baseName: match[2],
    };
  }
  return { prefix: "dataset", baseName: fieldName };
}

/**
 * Builds a field name with an optional resource prefix.
 *
 * @param baseName - The base field name (e.g., "tags", "metadata")
 * @param prefix - Optional resource prefix (e.g., "file", "dataset")
 * @returns The constructed field name
 *
 * @example
 * ```typescript
 * buildFieldName("tags", "file") // "file.tags"
 * buildFieldName("tags", "") // "tags"
 * buildFieldName("tags") // "tags"
 * buildFieldName("metadata", "dataset") // "dataset.metadata"
 * ```
 */
export function buildFieldName(
  baseName: string,
  prefix?: ResourcePrefix,
): string {
  if (!prefix) {
    return baseName;
  }
  return `${prefix}.${baseName}`;
}

/**
 * Extracts just the resource prefix from a field name.
 *
 * This helper returns the explicit prefix from the field name if present,
 * otherwise null when the field is unprefixed.
 *
 * @param fieldName - The field name to extract prefix from
 * @returns The resource prefix or null if no prefix
 *
 * @example
 * ```typescript
 * extractResourcePrefix("file.tags") // "file"
 * extractResourcePrefix("dataset.tags") // "dataset"
 * extractResourcePrefix("tags") // null
 * ```
 */
export function extractResourcePrefix(
  fieldName: string,
): ResourcePrefix | null {
  const match = fieldName.match(/^(dataset|file)\./);
  if (!match) {
    return null;
  }
  return match[1] as ResourcePrefix;
}

/**
 * Returns the SearchFieldType for a given field name, stripping any resource prefix first.
 *
 * This function:
 * 1. Strips the resource prefix (if present) from the field name
 * 2. Looks up the base field name in the searchFields array
 * 3. Returns the corresponding SearchFieldType
 *
 * @param fieldName - The field name (with or without prefix)
 * @returns The SearchFieldType ("date" | "metadata" | "tags" | "other")
 *
 * @example
 * ```typescript
 * searchFieldToType("file.tags") // "tags"
 * searchFieldToType("tags") // "tags"
 * searchFieldToType("dataset.metadata") // "metadata"
 * searchFieldToType("created") // "date"
 * searchFieldToType("unknown") // "other"
 * ```
 */
export function searchFieldToType(fieldName: string): SearchFieldType {
  // Strip prefix if present
  const { baseName } = parseFieldName(fieldName);

  // Look up the base field name in searchFields
  for (let i = 0; i < searchFields.length; i++) {
    const searchField = searchFields[i];
    if (searchField.name === baseName) {
      return searchField.type;
    }
  }

  // Default to "other" if not found
  return "other";
}
